"use client";

/**
 * Calendar Event Card
 * A single event block rendered inside a calendar column or all-day row.
 * Shared between desktop, mobile, and the unscheduled section.
 */

import type React from "react";
import { MapPin } from "lucide-react";
import type { ScheduleItemRow } from "../../../../_logic/types/db-types";
import { formatScheduleTime } from "../../../../_logic/utils/schedule-utils";
import { ItemDeleteButton } from "./ItemDeleteButton";

/** Hex-encoded alpha applied to a label color for the event card background (≈12% opacity). */
const EVENT_BG_ALPHA_HEX = "1f";

interface CalendarEventCardProps {
  item: ScheduleItemRow;
  color: string;
  onDelete?: (item: ScheduleItemRow) => void;
  onItemClick?: (item: ScheduleItemRow) => void;
  timeZone: string | null;
  /** When true, hides the time line (used in the all-day row / unscheduled section). */
  allDay?: boolean;
  /** When true, hides the time line regardless of `allDay` (the section's "Show time labels" toggle is off). */
  hideTime?: boolean;
  /**
   * Render a tight single-line layout (title + time inline, no location) with
   * reduced padding. Used when the card is too short (≤ ~30-minute slots) to
   * fit stacked title/time lines without overflow. The line truncates on the
   * very shortest cards where only the title fits.
   */
  compact?: boolean;
  /**
   * Shrink title + time text by one step. Set when the card shares its time
   * slot with sibling events (2+ lanes), so the narrower column doesn't force
   * the default `text-sm` title to wrap mid-word.
   */
  narrow?: boolean;
  /** When true, render an emerald ring + inline "Registered" pill to mark viewer-registered items. */
  isRegistered?: boolean;
  /** When true, render an amber ring + inline "Waitlisted" pill (viewer signed up but the slot was full). */
  isWaitlisted?: boolean;
  /** When true, render an inline "Sign up" pill (item has a linked activity the viewer hasn't joined). */
  canSignUp?: boolean;
  /** When true, render an inline "Signups closed" pill (linked activity's signups are closed). */
  isSignupClosed?: boolean;
}

/**
 * Renders a single event card.
 * @param item - The schedule item
 * @param color - Hex color used to tint the background (validated as 6-digit hex; falls back to indigo-500 if invalid)
 * @param onDelete - When provided, shows a hover-visible delete affordance
 * @param onItemClick - When provided, the card becomes a single `<button>` with an aria-label; delete is a sibling
 * @param timeZone - Page timezone for time label rendering
 * @param allDay - Suppresses the time line (for all-day rows / unscheduled section)
 * @param hideTime - Suppresses the time line regardless of `allDay` (section's toggle is off)
 */
export function CalendarEventCard({
  item,
  color,
  onDelete,
  onItemClick,
  timeZone,
  allDay,
  hideTime,
  compact,
  narrow,
  isRegistered,
  isWaitlisted,
  canSignUp,
  isSignupClosed,
}: CalendarEventCardProps): React.ReactElement {
  const titleSize = narrow ? "text-[11px]" : "text-sm";
  const metaSize = narrow ? "text-[10px]" : "text-xs";
  const compactTitleSize = narrow ? "text-[11px]" : "text-xs";
  const compactMetaSize = narrow ? "text-[10px]" : "text-[11px]";
  const timeLabel =
    !allDay && !hideTime ? formatScheduleTime(item.start_time, item.end_time, { timeZone, date: item.date }) : null;
  // Defense-in-depth: only interpolate validated hex colors into CSS; otherwise fall back to a safe neutral.
  const safeColor = /^#[0-9a-fA-F]{6}$/.test(color) ? color : "#6366f1";
  const ariaLabel = [item.title, timeLabel ?? (allDay ? "All day" : null), item.location ? `at ${item.location}` : null]
    .filter(Boolean)
    .join(", ");

  const statusPill = isRegistered ? (
    <span
      aria-hidden="true"
      className="mt-0.5 inline-flex w-fit items-center rounded-full bg-emerald-100 px-1.5 py-0 text-[10px] font-semibold leading-4 text-emerald-700"
    >
      ✓ Registered
    </span>
  ) : isWaitlisted ? (
    <span
      aria-hidden="true"
      className="mt-0.5 inline-flex w-fit items-center rounded-full bg-amber-100 px-1.5 py-0 text-[10px] font-semibold leading-4 text-amber-700"
    >
      Waitlisted
    </span>
  ) : isSignupClosed ? (
    <span
      aria-hidden="true"
      className="mt-0.5 inline-flex w-fit items-center rounded-full bg-gray-100 px-1.5 py-0 text-[10px] font-semibold leading-4 text-gray-500"
    >
      Signups closed
    </span>
  ) : canSignUp ? (
    <span
      aria-hidden="true"
      className="mt-0.5 inline-flex w-fit items-center rounded-full bg-gray-100 px-1.5 py-0 text-[10px] font-semibold leading-4 text-gray-700"
    >
      Sign up
    </span>
  ) : null;

  const visibleContent = compact ? (
    <>
      <p aria-hidden="true" className={`truncate leading-tight text-gray-900 ${compactTitleSize}`}>
        <span className="font-semibold">{item.title}</span>
        {timeLabel && <span className={`ml-1.5 font-medium text-gray-700 ${compactMetaSize}`}>{timeLabel}</span>}
      </p>
      {statusPill}
    </>
  ) : (
    <>
      <p aria-hidden="true" className={`break-words font-semibold leading-tight text-gray-900 ${titleSize}`}>
        {item.title}
      </p>
      {timeLabel && (
        <p aria-hidden="true" className={`break-words font-medium leading-tight text-gray-700 ${metaSize}`}>
          {timeLabel}
        </p>
      )}
      {item.location && (
        <p aria-hidden="true" className={`break-words font-medium leading-tight text-gray-500 ${metaSize}`}>
          <MapPin className="mr-1 inline-block h-3 w-3 align-[-2px]" aria-hidden="true" />
          {item.location}
        </p>
      )}
      {statusPill}
    </>
  );

  const surfaceStyle = { backgroundColor: `${safeColor}${EVENT_BG_ALPHA_HEX}` };
  // `justify-start` (not `justify-center`) keeps the title fully visible on the
  // shortest cards — if the slot is tighter than the rendered content, the time
  // line clips at the bottom instead of cropping the title at the top.
  const baseSurfaceClasses = compact
    ? "flex h-full w-full flex-col justify-start overflow-hidden rounded-md px-2 py-0.5 text-left"
    : "flex h-full w-full flex-col overflow-hidden rounded-md px-2 py-1.5 text-left";
  const surfaceClasses = isRegistered
    ? `${baseSurfaceClasses} ring-1 ring-emerald-500/60`
    : isWaitlisted
      ? `${baseSurfaceClasses} ring-1 ring-amber-500/60`
      : baseSurfaceClasses;

  return (
    <div className="group/item relative h-full w-full">
      {onItemClick ? (
        <button
          type="button"
          onClick={() => onItemClick(item)}
          aria-label={
            isRegistered
              ? `${ariaLabel}, registered`
              : isWaitlisted
                ? `${ariaLabel}, waitlisted`
                : isSignupClosed
                  ? `${ariaLabel}, signups closed`
                  : ariaLabel
          }
          className={`${surfaceClasses} touch-manipulation cursor-pointer transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-1`}
          style={surfaceStyle}
        >
          {visibleContent}
        </button>
      ) : (
        <div className={surfaceClasses} style={surfaceStyle}>
          {visibleContent}
        </div>
      )}
      {onDelete && (
        <div className="absolute right-0.5 top-0.5">
          <ItemDeleteButton item={item} onDelete={onDelete} />
        </div>
      )}
    </div>
  );
}
